// Wire types mirroring the backend domain types (hand-kept;
// docs/FRONTEND.md). Times are unix epoch milliseconds.

export type MeetingState =
  | "recording"
  | "importing"
  | "summarizing"
  | "complete"
  | "failed";

export interface Meeting {
  id: number;
  user_id: number;
  title: string;
  state: MeetingState;
  error: string | null;
  billed_stt_seconds: number;
  created_at: number;
  updated_at: number;
  kind: "solo" | "hosted";
  join_code: string | null;
}

export interface Segment {
  id: number;
  meeting_id: number;
  start_ms: number;
  end_ms: number;
  text: string;
  participant_id: number | null;
  speaker: string | null;
}

// ---- hosted meetings (plan 0002) ----

export interface Participant {
  id: number;
  meeting_id: number;
  user_id: number | null;
  display_name: string;
  role: "host" | "member" | "guest";
  state: "active" | "left" | "removed";
  mic_revoked: boolean;
  joined_at: number;
}

/** Presence marker interleaved with the transcript (plan 0004). */
export interface MeetingEvent {
  id: number;
  meeting_id: number;
  at_ms: number;
  kind: "joined" | "left" | "removed";
  participant_id: number | null;
  display_name: string;
}

export interface JoinInfo {
  meeting_id: number;
  title: string;
  host: string;
}

export interface JoinResult {
  meeting_id: number;
  title: string;
  participant: Participant;
  guest_token: string | null;
}

export interface RtcToken {
  url: string;
  token: string;
  room: string;
  identity: string;
  /** Server clock at mint + meeting start: anchors the capture
   *  sample-clock to meeting time (cross-speaker ordering). */
  server_now_ms: number;
  meeting_started_ms: number;
}

export interface SummaryRow {
  id: number;
  meeting_id: number;
  status: "pending" | "running" | "complete" | "failed";
  body_json: string | null;
  error: string | null;
  updated_at: number;
}

export interface SummaryBody {
  overview: string;
  key_points: string[];
  decisions: string[];
  action_items: { text: string; owner: string | null }[];
}

export interface SearchHit {
  meeting_id: number;
  meeting_title: string;
  segment_id: number;
  start_ms: number;
  snippet: string; // HTML-escaped; only <mark> markup
}

export interface User {
  id: number;
  email: string;
}

export interface MeetingDetail {
  meeting: Meeting;
  segments: Segment[];
  summary: SummaryRow | null;
  events: MeetingEvent[];
}

export interface IngestOutcome {
  duplicate: boolean;
  billed_seconds: number;
  segment_count: number;
}

export interface ApiErrorBody {
  error: { code: string; message: string };
}

export function parseSummaryBody(row: SummaryRow | null): SummaryBody | null {
  if (!row?.body_json) return null;
  try {
    return JSON.parse(row.body_json) as SummaryBody;
  } catch {
    return null;
  }
}

export function fmtMs(ms: number): string {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
